import { useEffect } from 'react';
import { CHAT_LOCALE, LANGUAGE } from '@constants/common.constants';
import { hasHideSurveyChat } from '@constants/common/miscellaneous';
import { canChatRefresh } from '@constants/configs/chatConfig';

const useNiceCXOneChat = (environment) => {

    useEffect(() => {

        try {
            const currentLang = environment?.language ?? LANGUAGE.ENGLISH;
            const lastLang = sessionStorage.getItem('cxone_lang');
            const chatActive = !!document.getElementById('cxone-guide-container');
            if (chatActive && lastLang && lastLang !== currentLang && canChatRefresh) {
                sessionStorage.setItem('cxone_lang', currentLang);
                window.location.reload();
                return () => {};
            }
            sessionStorage.setItem('cxone_lang', currentLang);
        } catch (_) { console.log('error: in useNiceCXOneChat'); }

        if (!environment || window.CXoneDfo || environment.flags?.isLegacyChatEnabled || !environment.flags?.isWebChatEnabled) {
            return () => {};
        }

        const {
            devEnvironment,
            links: {
                CHAT_TEMPLATE_LOGIN_EN,
                CHAT_TEMPLATE_LOGIN_ES,
                CHAT_TEMPLATE_NOT_LOGIN_EN,
                CHAT_TEMPLATE_NOT_LOGIN_ES,
                CHAT_ENTRYPOINT,
                CHAT_INIT
            },
            flags: {
                isWebChatEnabled,
                isWebChatEnglishEnabled,
                isWebChatSpanishEnabled
            },
            impersonation,
            impersonationDetails,
            language,
            stateCode,
            user
        } = environment;

        const isAcceptableProdRole = ['AGENCY_MANAGER', 'ASSISTER', 'ASSISTERENROLLMENTENTITY', 'BROKER', 'ENROLLMENT_PARTNER', 'INDIVIDUAL']; 

        if(!devEnvironment && user && !isAcceptableProdRole.includes(user.role.toUpperCase())) {
            return () => {};
        };

        const getConfigLanguage = () => {
            if(isWebChatEnabled && isWebChatEnglishEnabled && isWebChatSpanishEnabled) {
                return language;
            } else if(isWebChatEnabled && isWebChatEnglishEnabled) {
                return LANGUAGE.ENGLISH;
            } else if(isWebChatEnabled && isWebChatSpanishEnabled) {
                return LANGUAGE.SPANISH;
            } else {
                return LANGUAGE.ENGLISH;
            }
        };

        const getTemplateNumber = (language, isLogin) => {
            if(isLogin) {
                switch (language) {
                    case LANGUAGE.ENGLISH:
                        return CHAT_TEMPLATE_LOGIN_EN;        
                    case LANGUAGE.SPANISH:
                        return CHAT_TEMPLATE_LOGIN_ES;
                }
            } else {
                switch (language) {
                    case LANGUAGE.ENGLISH:
                        return CHAT_TEMPLATE_NOT_LOGIN_EN;
                    case LANGUAGE.SPANISH:
                        return CHAT_TEMPLATE_NOT_LOGIN_ES;
                }
            }
        };

        const getChatLocale = (configLanguage) => {
            switch (configLanguage) {
                case LANGUAGE.ENGLISH:
                    return CHAT_LOCALE.EN;
                case LANGUAGE.SPANISH:
                    return CHAT_LOCALE.ES;
                default:
                    return CHAT_LOCALE.EN;
            }
        };

        // Keeps the chat's bundled react-intl in its own context instead of the SPA's shared
        // window.__REACT_INTL_CONTEXT__. react-intl reads this once at load time, so it must be
        // set before the loader script is appended below. The host SPA's react-intl is already
        // loaded, so it is unaffected. HIX-226504.
        window.__REACT_INTL_BYPASS_GLOBAL_CONTEXT__ = true;
        window.CXoneDfo = 'cxone';
        window.cxone = window.cxone || function () {
            (window.cxone.q = window.cxone.q || []).push(arguments);
        };

        window.cxone.u = 'https://web-modules-de-na1.niceincontact.com/loader/1/loader.js';
        const script = document.createElement('script');
        script.type = 'module';
        script.src = `${window.cxone.u}?${Math.round(Date.now() / 1e3 / 3600)}`;

        document.head.appendChild(script);
        const hidePreSurvey = () => {
            if (hasHideSurveyChat.includes(stateCode) && !user) window.cxone('guide','hidePreSurvey', '.');
        };

        script.onload = () => {
            const configLanguage = getConfigLanguage();
            const locale = getChatLocale(configLanguage);
            const params = {};
            let userInfo = {};
            const uid = new URLSearchParams(window.location.search).get('u');
            const templateId = getTemplateNumber(configLanguage, !!user);
            window.cxone('init', CHAT_INIT, { rulesEngine: false, analyticsEvents: false });
    
            if(uid) { //outbound campaign
                window.cxone('guide', 'init',  { templateId, locale });
                window.cxone('guide','hidePreSurvey', '.');   
                window.cxone('guide', 'startChatEntrypoint', CHAT_ENTRYPOINT); //use pointOfContact
                window.cxone('chat','setCaseCustomField', 'dynamic_data', `u=${uid}`);
            } else { //inbound campaign
                window.cxone('guide', 'init', { templateId, locale });// use TemplateId, template contains pointOfContact, you launch pointOfContact directly or template directly 
                hidePreSurvey();
                window.cxone('guide', 'openMenu');
                window.cxone('chat','setCaseCustomField', 'landingpage', window.location.href);
                window.cxone('chat','setCaseCustomField', 'language', configLanguage);
                if(user) { 
                    let capUrl, fullName, userId, activeModuleName, activeModuleId, externalHouseholdCaseId, impersonatedUserType, impersonatedUsername;
                    capUrl = `${window.location.host}/cap/consumer/view/${user.encryptedUserName}`;
                    fullName = impersonation ? `${impersonationDetails.firstName} ${impersonationDetails.lastName}` : `${user.firstName} ${user.lastName}`; 
                    userId = impersonation ? impersonationDetails.userId : user.userId;
                    activeModuleName = user.activeModuleName;
                    activeModuleId = user.activeModuleId;
                    externalHouseholdCaseId = impersonation ? impersonationDetails.externalHouseholdCaseId : user.externalHouseholdCaseId; 
                    impersonatedUserType = impersonation ? user.role : null;
                    impersonatedUsername = impersonation ? user.userName : null;
                    const username = sessionStorage.getItem('householdEmail') || null;
                    let ani = sessionStorage.getItem('householdPhone') || null;

                    if (ani !== null) {
                        ani = ani.slice(0,10).padStart(10, '0');
                    }

                    const userInfo = {
                        'UserID': userId,
                        'activeModuleName':  activeModuleName,
                        'activeModuleID': activeModuleId,
                        'fullName':  fullName,
                        'externalHouseholdCaseID': externalHouseholdCaseId,
                        'capUrl': capUrl,
                        'isSessionImpersonated': impersonation,
                        'impersonatedUserType': impersonatedUserType,
                        'impersonatedUsername': impersonatedUsername,
                        'ani': ani,
                        'username': username                
                    };

                    window.cxone('chat','setCaseCustomField', 'dynamic_data', JSON.stringify(userInfo));    
                    window.cxone('chat','setCaseCustomField', 'customer_name', fullName);            
                }
                                     
            }

            script.onerror = () => {
                console.error('CXoneChat script failed to load.');
            };
        };

        return () => {
            document.head.removeChild(script);
        };
    }, [ environment ]);
  
};

export default useNiceCXOneChat;
